import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { saveAs } from 'file-saver';
import PlanCard from '../components/PlanCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { format } from 'date-fns';

export default function Subscribers() {
  const [prices, setPrices] = useState([
    { plan: '1-month', amount: 399, currency: 'NGN' },
    { plan: '6-month', amount: 1499, currency: 'NGN' },
    { plan: '1-year', amount: 2999, currency: 'NGN' },
  ]);
  const [newPrices, setNewPrices] = useState({
    '1-month': 399,
    '6-month': 1499,
    '1-year': 2999,
  });
  const [subscribers, setSubscribers] = useState({
    users: [],
    total: 0,
    page: 1,
    limit: 10,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('adminToken');

  const formatCurrency = (amount, currency) => {
    if (currency === 'NGN') {
      return `₦${(amount / 100).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
    }
    return `${currency} ${(amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  const fetchPricing = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await axios.get('https://awriter-backend-si7g.onrender.com/api/admin/pricing', {
        headers: { Authorization: `Bearer ${token}` },
      });

      setPrices(response.data.prices);
      setNewPrices(response.data.prices.reduce((acc, p) => ({
        ...acc,
        [p.plan]: p.amount,
      }), {}));
    } catch (error) {

      setErrorMessage(error.response?.data?.error || 'Failed to fetch pricing data');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubscribers = async (page = 1, query = '') => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await axios.get(
        `https://awriter-backend-si7g.onrender.com/api/admin/subscribers?page=${page}&limit=10${query ? `&email=${encodeURIComponent(query)}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSubscribers({
        users: response.data.users || [],
        total: response.data.total || 0,
        page: response.data.page || 1,
        limit: response.data.limit || 10,
      });
    } catch (error) {
      console.error('Error fetching subscribers:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to fetch subscribers');
      setSubscribers({ users: [], total: 0, page: 1, limit: 10 });
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePriceChange = (plan, value) => {
    setNewPrices(prev => ({
      ...prev,
      [plan]: value ? parseFloat(value) * 100 : prev[plan],
    }));
  };

  const handleUpdatePrices = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      const pricesToUpdate = Object.entries(newPrices).map(([plan, amount]) => ({
        plan,
        amount,
      }));
      const response = await axios.patch(
        'https://awriter-backend-si7g.onrender.com/api/admin/pricing',
        { prices: pricesToUpdate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchPricing(); 
    } catch (error) {
      console.error('Error updating prices:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to update prices');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSubscribers(1, searchQuery);
  };

  const handleExportCSV = () => {
    const headers = ['ID,Email,First Name,Last Name,Plan,End Date,Trial Count'];
    const rows = subscribers.users.map(user => [
      user.id,
      user.email,
      user.firstName || '',
      user.lastName || '',
      user.subscriptionPlan || 'None',
      user.subscriptionEndDate ? format(new Date(user.subscriptionEndDate), 'yyyy-MM-dd') : 'N/A',
      user.trialCount || 0,
    ].map(field => `"${field}"`).join(','));
    const csv = [...headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, 'subscribers.csv');
  };

  useEffect(() => {
    fetchPricing();
    fetchSubscribers();
    window.scrollTo(0, 0);
  }, []);

  const handlePageChange = (newPage) => {
    fetchSubscribers(newPage, searchQuery);
  };

  return (
    <div className="space-y-10 mt-5">
      {/* Title */}
      <div>
        <h2 className="text-2xl  font-bold mb-2">Subscribers</h2>
        <p className="text-gray-600">All subscribed users and plans</p>
      </div>

      {/* Subscription Plans */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Subscription Plans</h3>
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="bg-gray-200 h-32 rounded-lg p-4 animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {prices.map(plan => (
                <PlanCard
                  key={plan.plan}
                  duration={plan.plan.replace('-', ' ').toUpperCase()}
                  price={formatCurrency(plan.amount, plan.currency)}
                />
              ))}
            </div>
            <form onSubmit={handleUpdatePrices} className="mt-4 bg-white p-4 rounded-lg shadow">
              <h4 className="text-md font-semibold mb-2">Update Plan Prices (in NGN)</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {prices.map(plan => (
                  <div key={plan.plan} className="flex flex-col">
                    <label className="text-sm font-medium">{plan.plan.replace('-', ' ').toUpperCase()}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={(newPrices[plan.plan] / 100).toFixed(2)}
                      onChange={(e) => handlePriceChange(plan.plan, e.target.value)}
                      className="border rounded p-2 mt-1"
                      placeholder="Enter price in NGN"
                    />
                  </div>
                ))}
              </div>
              <button
                type="submit"
                className="mt-4 bg-[#55A55E] text-white px-4 py-2 rounded hover:bg-[#46824e]"
                disabled={isLoading}
              >
                {isLoading ? 'Updating...' : 'Update Prices'}
              </button>
            </form>
          </>
        )}
      </div>

      {/* Subscriber List */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Active Subscribers</h3>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            {/* <form onSubmit={handleSearch} className="flex items-center">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by email..."
                className="border rounded p-2 mr-2"
              />
              <button
                type="submit"
                className="bg-[#55A55E] text-white px-4 py-2 rounded hover:bg-[#46824e]"
              >
                Search
              </button>
            </form> */}
            <button
              onClick={handleExportCSV}
              className="bg-[#6a6a6a] text-white px-4 py-2 rounded hover:bg-[#000000]"
            >
              Export to CSV
            </button>
          </div>
          {isLoading ? (
            <LoadingSpinner />
          ) : errorMessage ? (
            <div className="p-4 bg-[#55A55E] text-white rounded text-center">{errorMessage}</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2 text-left">Email</th>
                      <th className="border p-2 text-left">First Name</th>
                      <th className="border p-2 text-left">Last Name</th>
                      <th className="border p-2 text-left">Plan</th>
                      <th className="border p-2 text-left">End Date</th>
                      <th className="border p-2 text-left">Trial Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.users.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="border p-2 text-center">
                          No subscribers found
                        </td>
                      </tr>
                    ) : (
                      subscribers.users.map(user => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="border p-2">{user.email}</td>
                          <td className="border p-2">{user.firstName || 'N/A'}</td>
                          <td className="border p-2">{user.lastName || 'N/A'}</td>
                          <td className="border p-2">{user.subscriptionPlan || 'None'}</td>
                          <td className="border p-2">
                            {user.subscriptionEndDate
                              ? format(new Date(user.subscriptionEndDate), 'yyyy-MM-dd')
                              : 'N/A'}
                          </td>
                          <td className="border p-2">{user.trialCount || 0}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-between items-center mt-4">
                <div>
                  Showing {(subscribers.page - 1) * subscribers.limit + 1} to{' '}
                  {Math.min(subscribers.page * subscribers.limit, subscribers.total)} of{' '}
                  {subscribers.total} subscribers
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(subscribers.page - 1)}
                    disabled={subscribers.page === 1}
                    className="bg-[#55A55E] text-white px-4 py-2 rounded disabled:bg-gray-300 hover:bg-[#46824e]"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(subscribers.page + 1)}
                    disabled={subscribers.page * subscribers.limit >= subscribers.total}
                    className="bg-[#55A55E] text-white px-4 py-2 rounded disabled:bg-gray-300 hover:bg-[#46824e]"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}